import React, { useCallback, useEffect, useState } from "react";
import { View, Text, TouchableOpacity, StyleSheet, Platform, Alert } from "react-native";
import { androidScreenTimeSupported, isDnsVpnRunning } from "../utils/androidScreenTime";
import { iosScreenTimeSupported } from "../utils/iosScreenTime";
import {
  isProtectionRunning,
  startProtection,
  stopProtection,
  syncRules,
} from "../platform/screenTimeBridge";
import { getBlocksTodayCount } from "../storage/blockStatsStorage";
import { loadLastRulesSyncAtMs } from "../storage/rulesRepository";
import {
  getAdvancedMediaKeywordsEnabled,
  getBlocklist,
  getFilterEnabled,
} from "../blocking/BlocklistManager";
import { buildParentalVpnSyncPayload } from "../blocking/parentalVpnPayload";

/**
 * Minimal UI: blocks today (aggregated) + start/stop native protection when the bridge exists.
 */
export function ProtectionBar() {
  const [blocksToday, setBlocksToday] = useState(0);
  const [running, setRunning] = useState(false);
  const [lastSyncAtMs, setLastSyncAtMs] = useState(null);
  const [busy, setBusy] = useState(false);
  const canToggle =
    (Platform.OS === "android" && androidScreenTimeSupported()) ||
    (Platform.OS === "ios" && iosScreenTimeSupported());

  const refresh = useCallback(async () => {
    try {
      setBlocksToday(await getBlocksTodayCount());
      setLastSyncAtMs(await loadLastRulesSyncAtMs());
      if (Platform.OS === "android" && androidScreenTimeSupported()) {
        setRunning(!!(await isDnsVpnRunning()));
      } else {
        setRunning(!!(await isProtectionRunning()));
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 4000);
    return () => clearInterval(id);
  }, [refresh]);

  const syncCurrentRules = async () => {
    const [domains, advancedOn] = await Promise.all([
      getBlocklist(),
      getAdvancedMediaKeywordsEnabled(),
    ]);
    const payload = await buildParentalVpnSyncPayload(domains, advancedOn);
    await syncRules(payload.block, payload.allow, payload.keywords, payload.categoryRules, {
      safeMode: payload.safeMode,
    });
  };

  const onToggle = async () => {
    if (!canToggle || busy) return;
    setBusy(true);
    try {
      if (running) {
        await stopProtection();
        setRunning(false);
        Alert.alert("Protection", "DNS filter stopped. VPN should disconnect shortly.");
      } else {
        // Always push current lists before starting VPN, otherwise filter has empty rules.
        await syncCurrentRules();
        const filterOn = await getFilterEnabled();
        if (!filterOn) {
          Alert.alert(
            "Filter is OFF",
            "Parental Filter is currently OFF. VPN will start with Adult-only defaults (porn sites). Turn Parental Filter ON and add sites (including YouTube) under Screen Time → Parental Filter for custom blocks.",
          );
        }
        await startProtection();
        Alert.alert(
          "Protection",
          "If Android asks, tap Allow / OK for the VPN connection. Then test a blocked site in Chrome.",
        );
      }
      await refresh();
    } catch (e) {
      Alert.alert("Protection", e?.message ? String(e.message) : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <View style={styles.wrap}>
      <Text style={styles.statsLine}>Blocks today (this app): {blocksToday}</Text>
      <Text style={styles.statusLine}>VPN: {running ? "running" : "stopped"}</Text>
      <Text style={styles.statusLine}>
        Rules last sync: {lastSyncAtMs ? new Date(lastSyncAtMs).toLocaleTimeString() : "not synced yet"}
      </Text>
      {canToggle ? (
        <TouchableOpacity
          style={[styles.btn, running ? styles.btnOff : styles.btnOn, busy && styles.btnBusy]}
          onPress={onToggle}
          disabled={busy}
        >
          <Text style={styles.btnText}>
            {busy ? "Working…" : running ? "Stop protection" : "Start protection"}
          </Text>
        </TouchableOpacity>
      ) : (
        <Text style={styles.muted}>
          {Platform.OS === "ios"
            ? "Install the iOS Network Extension + IosScreenTimeControl module (see native-ios/IOS_BUILD_NOTES.txt) to enable system DNS filtering."
            : "Protection controls unavailable in this build."}
        </Text>
      )}
      <Text style={styles.hint}>
        {Platform.OS === "android"
          ? "1) Screen Time → Parental Filter → turn ON + add sites  2) Tap Start protection  3) Allow VPN"
          : "iOS: requires Apple-approved Network Extension entitlements."}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    marginBottom: 12,
    padding: 12,
    backgroundColor: "#F3F4F6",
    borderRadius: 10,
  },
  statsLine: { fontSize: 14, color: "#111", marginBottom: 8 },
  statusLine: { fontSize: 12, color: "#374151", marginBottom: 4 },
  btn: { paddingVertical: 10, borderRadius: 8, alignItems: "center" },
  btnOn: { backgroundColor: "#1ABC9C" },
  btnOff: { backgroundColor: "#E74C3C" },
  btnBusy: { opacity: 0.7 },
  btnText: { color: "#fff", fontWeight: "600" },
  hint: { marginTop: 8, fontSize: 11, color: "#6B7280" },
  muted: { fontSize: 12, color: "#6B7280" },
});
